import React from "react";
import { LoginContextData, ITwoFAPayload } from "../Declarations";
import { useQRCode } from "../hooks/useQRCode";
import { useSecretKey } from "../hooks/useSecretKey";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

interface IConfigure2FAProps {
    data: LoginContextData;
    onComplete: () => void;
}

export const Configure2FA = ({ data, onComplete }: IConfigure2FAProps) => {
    // Guard: validate required input
    const hasBaseFields =
        data.UserID !== undefined &&
        data.UserName !== undefined &&
        data.Password !== undefined &&
        data.BrowserFingerprint !== undefined;

    const userAgent = navigator.userAgent;

    const basePayload = {
        BrowserFingerprint: data.BrowserFingerprint !== undefined ? data.BrowserFingerprint : "",
        Password: data.Password !== undefined ? data.Password : "",
        UserAgent: userAgent,
        UserID: data.UserID !== undefined ? data.UserID : 0,
        UserName: data.UserName !== undefined ? data.UserName : "",
    };

    // Get secret key first
    const {
        data: secretKey,
        isLoading: keyLoading,
        isError: keyError,
    } = useSecretKey(basePayload);

    // Only call QR code generation if we have a secret key
    const qrPayload: ITwoFAPayload & { SecretKey: string } = {
        ...basePayload,
        SecretKey: secretKey !== undefined ? secretKey : "",
    };

    const {
        data: qrCode,
        isLoading: qrLoading,
        isError: qrError,
    } = useQRCode(qrPayload, secretKey !== undefined); // use enabled flag inside hook

    const isLoading = !hasBaseFields || keyLoading || qrLoading;
    const hasError = keyError || qrError;

    if (isLoading) {
        return <p>Loading 2FA setup...</p>;
    }

    if (hasError) {
        return <p className="error">Failed to load 2FA configuration.</p>;
    }

    return (
        <div>
            <Typography sx={{ fontWeight: "bold" }} variant="h5">Set Up Two-Factor Authentication</Typography>
            {qrCode !== undefined && (
                <img alt="2FA QR Code" src={`data:image/png;base64,${qrCode}`} />
            )}
            <p>
                Manual Code: <strong>{secretKey}</strong>
            </p>
            <Button
                color="primary"
                fullWidth={false}
                variant="contained"
                onClick={onComplete}
            >Continue</Button>
        </div>
    );
};
